import { StarIcon as StarIconFilled } from '@heroicons/react/24/solid'
import { StarIcon as StarIconOutline } from '@heroicons/react/24/outline'
import { useEffect, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import Feedback from '../components/ReviewRequest/Feedback'
import useReviewRequest from '../hooks/useReviewRequest'
import useUpdateReviewRequestContact from '../hooks/useUpdateReviewRequestFeedback'

const ReviewRequest = () => {
  const timeoutRef = useRef()
  const { requestSlug, feedbackSlug: defaultFeedbackSlug } = useParams()

  const [feedbackSlug, setFeedbackSlug] = useState(defaultFeedbackSlug)
  const [rating, setRating] = useState(
    new URLSearchParams(useLocation().search).get('rating') || 0
  )
  const [shouldRedirect, setShouldRedirect] = useState(false)

  const [reviewRequest, { isLoading }] = useReviewRequest(requestSlug)
  const [updateReviewRequestContact, { latestSlug }] =
    useUpdateReviewRequestContact(requestSlug, feedbackSlug)

  const hasPositiveRating = Boolean(rating >= 4)
  const hasNegativeRating = Boolean(rating > 0 && rating < 4)

  useEffect(() => {
    const currentTimeout = timeoutRef.current

    _updateTimeout()

    return () => {
      if (!currentTimeout) {
        return
      }

      clearTimeout(currentTimeout)
    }
  }, [rating]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!rating) {
      return
    }

    updateReviewRequestContact({ rating })
  }, [rating]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoading || !shouldRedirect || !reviewRequest.endpoint) {
      return
    }

    window.location = reviewRequest.endpoint
  }, [isLoading, reviewRequest.endpoint, shouldRedirect])

  useEffect(() => {
    if (!latestSlug) {
      return
    }

    setFeedbackSlug(latestSlug)
  }, [latestSlug])

  const _updateTimeout = () => {
    setShouldRedirect(false)

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (rating >= 4) {
      timeoutRef.current = setTimeout(() => setShouldRedirect(true), 2000)
    }
  }

  const hasLogo = Boolean(reviewRequest?.logo)
  const willRedirect = Boolean(hasPositiveRating && reviewRequest.endpoint)
  const needsFeedback = Boolean(
    hasNegativeRating || (hasPositiveRating && !reviewRequest.endpoint)
  )

  return (
    <div className="h-screen flex flex-col gap-10 items-center justify-center px-2 py-4 text-gray-900">
      <div className="flex flex-col gap-4 items-center">
        {hasLogo && (
          <img
            alt={reviewRequest.location_name}
            className="w-[240px] h-[120px] mb-8 object-contain"
            src={reviewRequest.logo.path}
          />
        )}
        <h4 className="font-bold text-center">
          Thank you for choosing {reviewRequest?.location_name}
        </h4>
        <h6 className="font-medium text-center">How was your experience?</h6>
      </div>
      <div className="grid grid-flow-col auto-cols-min gap-2">
        {[...Array(5)].map((_, index) => {
          const handleClick = () => setRating(index + 1)

          return (
            <button
              className="appearance-none w-8 h-8"
              key={`star-${index}`}
              onClick={handleClick}
              type="button">
              {rating > index ? (
                <StarIconFilled className="w-8 h-8 text-yellow-500" />
              ) : (
                <StarIconOutline className="w-8 h-8 text-yellow-500" />
              )}
            </button>
          )
        })}
      </div>
      {willRedirect && (
        <div className="flex flex-col gap-4 items-center py-8 max-w-xs">
          <h6 className="font-medium text-center">
            That&rsquo;s great to hear!
            <br />
            <br />
            We will now redirect you to Google, and would love if you could
            leave us a review!
          </h6>
          <span className="spinner" />
        </div>
      )}
      {needsFeedback && (
        <Feedback
          feedbackSlug={feedbackSlug}
          rating={rating}
          requestSlug={requestSlug}
        />
      )}
      <div className="w-full md:w-[60%] border-t my-2" />
      <p>
        Powered by{' '}
        <a
          className="text-indigo-600 font-medium"
          href="https://meetmadison.ai"
          rel="noreferrer"
          target="_blank">
          Madison
        </a>
      </p>
    </div>
  )
}

export default ReviewRequest
